import { useState, useEffect, useCallback } from 'react';
// material
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  Container,
  Typography,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TableCell,
  Checkbox,
  Box
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFoundAgreement from '../../components/SearchNotFoundAgreement';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import BrudCrumbs from '../../components/BreadCrumbs';
import PageTitle from '../../components/PageHeading';
import { fDate } from '../../utils/formatTime';
import DataToolbar from './DataToolbar';
import { StyledTableCell, StyledTableRow } from '../../utils/StyleTable';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getBankDropdownData, saveApiData } from '../../store/Slice/Global/GlobalSlice';
import SimplePagination from '../../components/SimplePagination';
import useDebounce from '../../utils/useDebounce';
import { getDataSourceList } from '../../store/Slice/DataSource/DataSourceSlice';
import useDataSourceSelector from '../../store/Slice/DataSource/DataSourceSelector';
import RevenueService from '../services/RevenueService';

// ----------------------------------------------------------------------
const TABLE_HEAD = [
  { id: 'datasetName', label: 'dataset name', alignRight: false },
  { id: 'uploadMethod', label: 'import method', alignRight: false },
  { id: 'noOfRecords', label: 'no.  of records', alignRight: false },
  { id: 'noOfColumns', label: 'no. of columns', alignRight: false },
  { id: 'uploadedOn', label: 'uploaded on', alignRight: false },
  { id: 'modifiedOn', label: 'modified on', alignRight: false },
  { id: 'status', label: 'status', alignRight: false },
  { id: 'action', label: 'ACTION' }
];

const SELECT_STATUS = {
  ACTIVE: {
    type_name: 'Active',
    type_code: 'ACTIVE	',
    type_id: 134
  },
  INACTIVE: {
    type_name: 'Inactive',
    type_code: 'INACTIVE	',
    type_id: 136
  },
  INCOMPLETE: {
    type_name: 'Incomplete',
    type_code: 'INCOMPLETE	',
    type_id: 138
  }
};

const getLabelColor = (status) => {
  if (status === 'NEW') return 'warning';
  if (status === 'ASSIGNED' || status === 'REASSIGNED') return 'success';
  return 'success';
};

export default function DataList() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectStatusValue, setSelectStatusValue] = useState(' ');
  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);
  const [filterParameter, setFilterParameter] = useState({
    bankId: null
  });
  const [hasNextPage, setHasNextPage] = useState(true);
  const [validateDialogOpen, setValidateDialogOpen] = useState(false);
  const [pendingValidateId, setPendingValidateId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const { DataSourceListData } = useDataSourceSelector();
  const { DownloadExportRecord } = RevenueService;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { LoginData } = useLoginSelector();
  const debouncedSearch = useDebounce(filterName, 1000);

  const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const getAgreementsListApi = useCallback(() => {
    let statusResult = null;

    if (selectStatusValue === 134) {
      statusResult = true;
    } else if (selectStatusValue === 136) {
      statusResult = false;
    }

    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        sortOrder: order,
        pageNumber: page + 1
      },
      filterModel: {
        status: statusResult,
        uploadMethod: filterParameter?.bankId || null
      }
    };

    dispatch(getDataSourceList(credentials));
  }, [
    LoginData,
    debouncedSearch,
    order,
    selectStatusValue,
    filterParameter,
    page,
    rowsPerPage,
    dispatch
  ]);

  useEffect(() => {
    getAgreementsListApi();
  }, [getAgreementsListApi]);

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [dispatch, LoginData]);

  useEffect(() => {
    getBankDropApi();
  }, [getBankDropApi]);

  const handleRequestSort = useCallback(
    (event, property) => {
      if (property !== 'action') {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
      }
    },
    [order, orderBy]
  );

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelecteds = DataSourceListData?.map((n) => n) || [];
        setSelected(newSelecteds);
        return;
      }
      setSelected([]);
    },
    [DataSourceListData]
  );

  useEffect(() => {
    setHasNextPage(DataSourceListData?.length >= rowsPerPage);
  }, [DataSourceListData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const handleClick = useCallback(
    (event, item) => {
      const selectedIndex = selected.indexOf(item);
      let newSelected = [];
      if (selectedIndex === -1) {
        newSelected = [...selected, item];
      } else {
        newSelected = selected.filter((s) => s !== item);
      }
      setSelected(newSelected);
    },
    [selected]
  );

  const handleOpenValidateDialog = useCallback((rowId) => {
    setPendingValidateId(rowId);
    setValidateDialogOpen(true);
  }, []);

  const handleConfirmValidate = useCallback(() => {
    setValidateDialogOpen(false);
    navigate(`/data/validate`, {
      state: { rowId: pendingValidateId }
    });
  }, [navigate, pendingValidateId]);

  const handleExport = async (type) => {
    console.log('type222', type);
    const credential = { exportType: type };
    try {
      const response = await DownloadExportRecord(credential, LoginData?.jwt);
      console.log('response222', response);
      const contentType = response.headers.get('content-type');
      const blob = await response.blob();
      let fileName = 'exportData';
      let mimeType = contentType;
      if (type === 'EXCEL') {
        fileName += '.xlsx';
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (type === 'PDF') {
        fileName += '.pdf';
        mimeType = 'application/pdf';
      }
      const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      handleClose();
    }
  };
  return (
    <Page title="Partner Managemet System || DATA SOURCE">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="DATA SOURCE" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Card>
            <DataToolbar
              numSelected={selected.length}
              filterName={filterName}
              setFilterName={setFilterName}
              parent="data"
              selectStatusValue={selectStatusValue}
              setSelectStatusValue={setSelectStatusValue}
              filterDatesRange={filterDatesRange}
              setFilterDatesRange={setFilterDatesRange}
              selectStatus={SELECT_STATUS}
              setFilterParameter={setFilterParameter}
              filterParameter={filterParameter}
              rowInfo={selected}
              onValidateClick={handleOpenValidateDialog}
              handleExport={handleExport}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800, minHeight: '50vh', overflow: 'auto' }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={DataSourceListData?.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    parent="dataSource"
                    isCheckBox="true"
                  />
                  <TableBody>
                    {DataSourceListData?.length > 0 &&
                      DataSourceListData?.map((item, index) => {
                        const isItemSelected = selected.indexOf(item) !== -1;

                        return (
                          <StyledTableRow
                            hover
                            key={index}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isItemSelected}
                                onChange={(event) => handleClick(event, item)}
                              />
                            </TableCell>
                            <StyledTableCell align="left">
                              {item.datasetName ? item.datasetName : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {item.importMethod ? item.importMethod : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {item.noOfRecords ? item.noOfRecords : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {item.totalColumn ? item.totalColumn : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {item.uploadedOn ? fDate(item.uploadedOn) : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {item.modifiedOn ? fDate(item.modifiedOn) : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              <Label variant="ghost" color={getLabelColor(item.status)}>
                                {item.status || 'N/A'}
                              </Label>
                            </StyledTableCell>
                            <StyledTableCell align="" width={115}>
                              <UserMoreMenu
                                partner={item}
                                rowId={item.id}
                                parent="data"
                                bankId={item.bankId}
                                bankName={item.bankName}
                              />
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                  </TableBody>
                  {DataSourceListData?.length === 0 && (
                    <TableBody>
                      <TableRow>
                        <StyledTableCell
                          align="center"
                          colSpan={TABLE_HEAD.length + 1}
                          sx={{ py: 3 }}
                        >
                          <SearchNotFoundAgreement searchQuery={filterName} />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>
            <SimplePagination page={page} setPage={setPage} hasNextPage={hasNextPage} />
          </Card>

          <Dialog fullWidth open={validateDialogOpen} onClose={() => setValidateDialogOpen(false)}>
            <DialogTitle>
              <Typography variant="h6">Validate Data</Typography>
            </DialogTitle>

            <DialogContent dividers>
              <Typography>Are you sure you want to validate this data?</Typography>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setValidateDialogOpen(false)}>Cancel</Button>

              <Button variant="contained" onClick={handleConfirmValidate}>
                Yes, Validate
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Page>
  );
}
